import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import { api, type UserSettings } from '../services/api.js';
import {
  TIMEOUT_OPTIONS,
  DEFAULT_TIMEOUT_INDEX,
  DEFAULT_CORE_TEMP_THRESHOLD,
  DEFAULT_MEMORY_TEMP_THRESHOLD,
  DEFAULT_ELECTRICITY_RATE,
} from '../utils/constants.js';

/** Debounce save — wait 500ms after last change before persisting */
function useDebouncedSave() {
  const timer = useRef<ReturnType<typeof setTimeout>>();
  return useCallback((settings: UserSettings) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.saveSettings(settings).catch(() => {});
    }, 500);
  }, []);
}

export function useSettings() {
  const [loaded, setLoaded] = useState(false);

  const [timeoutIndex, setTimeoutIndex] = useState(DEFAULT_TIMEOUT_INDEX);
  const [coreTempThreshold, setCoreTempThreshold] = useState(DEFAULT_CORE_TEMP_THRESHOLD);
  const [memoryTempThreshold, setMemoryTempThreshold] = useState(DEFAULT_MEMORY_TEMP_THRESHOLD);
  const [theme, setThemeState] = useState('dark');
  const [electricityRate, setElectricityRateState] = useState(DEFAULT_ELECTRICITY_RATE);
  const [usdRub, setUsdRub] = useState(82);

  const debouncedSave = useDebouncedSave();

  // Load settings from server on mount
  useEffect(() => {
    api.getSettings().then((s) => {
      const idx = TIMEOUT_OPTIONS.indexOf(s.offlineTimeout as typeof TIMEOUT_OPTIONS[number]);
      setTimeoutIndex(idx >= 0 ? idx : DEFAULT_TIMEOUT_INDEX);
      setCoreTempThreshold(s.coreTempThreshold);
      setMemoryTempThreshold(s.memoryTempThreshold);

      // Default to dark — reset any previously-saved theme once per browser
      let theme = s.theme || 'dark';
      try {
        if (typeof window !== 'undefined' && !window.localStorage.getItem('cd_theme_reset_v1')) {
          theme = 'dark';
          window.localStorage.setItem('cd_theme_reset_v1', '1');
          if (s.theme !== 'dark') {
            api.saveSettings({ ...s, theme: 'dark' }).catch(() => {});
          }
        }
      } catch { /* ignore localStorage errors */ }
      setThemeState(theme);

      setElectricityRateState(s.electricityRate);
      if ((s as any).usdRub > 30) setUsdRub((s as any).usdRub);
      document.documentElement.setAttribute('data-theme', theme);
      setLoaded(true);
    }).catch(() => {
      setLoaded(true);
    });
  }, []);

  const save = useCallback((patch: Partial<UserSettings>) => {
    const current: UserSettings = {
      theme,
      offlineTimeout: TIMEOUT_OPTIONS[timeoutIndex],
      coreTempThreshold,
      memoryTempThreshold,
      electricityRate,
      ...patch,
    };
    debouncedSave(current);
  }, [theme, timeoutIndex, coreTempThreshold, memoryTempThreshold, electricityRate, debouncedSave]);

  const offlineThreshold = TIMEOUT_OPTIONS[timeoutIndex] * 60 * 1000;

  const setTimeoutByIndex = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(idx, TIMEOUT_OPTIONS.length - 1));
    setTimeoutIndex(clamped);
    save({ offlineTimeout: TIMEOUT_OPTIONS[clamped] });
  }, [save]);

  const setCoreTemp = useCallback((v: number) => {
    setCoreTempThreshold(v);
    save({ coreTempThreshold: v });
  }, [save]);

  const setMemoryTemp = useCallback((v: number) => {
    setMemoryTempThreshold(v);
    save({ memoryTempThreshold: v });
  }, [save]);

  const setTheme = useCallback((t: string) => {
    setThemeState(t);
    document.documentElement.setAttribute('data-theme', t);
    save({ theme: t });
  }, [save]);

  const toggleTheme = useCallback(() => {
    const themes = ['dark', 'classic', 'oled'];
    const idx = themes.indexOf(theme);
    const next = themes[(idx + 1) % themes.length];
    setTheme(next);
    return next;
  }, [theme, setTheme]);

  const setElectricityRate = useCallback((v: number) => {
    setElectricityRateState(v);
    save({ electricityRate: v });
  }, [save]);

  return {
    loaded,
    timeoutIndex,
    timeoutMinutes: TIMEOUT_OPTIONS[timeoutIndex],
    offlineThreshold,
    setTimeoutByIndex,
    coreTempThreshold,
    memoryTempThreshold,
    setCoreTemp,
    setMemoryTemp,
    theme,
    setTheme,
    toggleTheme,
    electricityRate,
    setElectricityRate,
    usdRub,
  };
}
